import { useCallback, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  updateNearByPlaceGpsEndpoint,
  getNearByPlaceByIdEndpoint,
} from '../services/nearByPlaceService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { setLocationAction } from '../../../redux/features/mapView';
import { AppStore } from '../../../redux/store';

export const useModelNearByPlaceMap = () => {
  const { id: nearByPlaceId = '' } = useParams();
  const dispatch = useDispatch();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const mapViewState = useSelector((store: AppStore) => store.mapView);

  const [errorAlert, setErrorAlert] = useState<any>(null);
  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({
    success: true,
    danger: false,
    title: '',
    text: '',
    onConfirm: () => setSweetAlert(false),
    onCancel: () => setSweetAlert(false),
  });

  const [nearByPlace, setNearByPlace] = useState<any>(null);

  const showAlert = useCallback((props: any) => {
    setAlertProps((state: any) => ({ ...state, ...props }));
    setSweetAlert(true);
  }, []);

  const loadNearPlace = useCallback(async () => {
    const { status, data } = await callEndpoint(
      getNearByPlaceByIdEndpoint(nearByPlaceId, token),
    );
    if (status !== 200) {
      setErrorAlert('No se pudo cargar la información del lugar.');
      showAlert({
        success: false,
        danger: true,
        title: 'Error',
        text: 'No se pudo cargar la información del lugar.',
      });
      return;
    }
    const response = fetchResponseAdapter(data);
    const found =
      ([] as any[])
        .concat(response?.data ?? [])
        .find((p: any) => `${p.id}` === `${nearByPlaceId}`) || null;
    if (found) {
      setNearByPlace(found);
    } else {
      setErrorAlert('Lugar no encontrado.');
      showAlert({
        success: false,
        danger: true,
        title: 'Error',
        text: 'Lugar no encontrado.',
      });
    }
  }, [token, callEndpoint, nearByPlaceId, showAlert]);

  useEffect(() => {
    loadNearPlace();
  }, [loadNearPlace]);

  useEffect(() => {
    const lat = nearByPlace?.gps_point?.lat ?? nearByPlace?.lat;
    const lng = nearByPlace?.gps_point?.lng ?? nearByPlace?.lng;

    if (lat !== undefined && lng !== undefined) {
      dispatch(
        setLocationAction({
          location: {
            lat: Number(lat),
            lng: Number(lng),
          },
        }),
      );
    }
  }, [nearByPlace, dispatch]);

  const handleSave = async () => {
    if (
      !mapViewState.location ||
      !mapViewState.location.lat ||
      !mapViewState.location.lng
    ) {
      setErrorAlert('Por favor seleccione una ubicación en el mapa');
      showAlert({
        success: false,
        danger: true,
        title: 'Error',
        text: 'Por favor seleccione una ubicación en el mapa',
      });
      return;
    }

    if (!nearByPlace?.name) {
      showAlert({
        success: false,
        danger: true,
        title: 'Error',
        text: 'El lugar no tiene nombre cargado.',
      });
      return;
    }

    const { status } = await callEndpoint(
      updateNearByPlaceGpsEndpoint(
        nearByPlaceId,
        {
          name: nearByPlace.name,
          lat: mapViewState.location.lat,
          lng: mapViewState.location.lng,
        },
        token,
      ),
    );

    if (status === 200) {
      showAlert({
        success: true,
        danger: false,
        title: 'OK',
        text: 'Ubicación actualizada correctamente',
        onConfirm: () => {
          setSweetAlert(false);
          navigate(-1);
        },
        onCancel: () => setSweetAlert(false),
      });
      return;
    }

    showAlert({
      success: false,
      danger: true,
      title: 'Error',
      text: 'No se pudo actualizar la ubicación',
    });
  };

  const handlePlaceSelect = (place: any) => {
    // Extract coordinates from the place object
    // The API returns coordinates as [lng, lat] in the location.coordinates array
    if (
      place.location?.coordinates &&
      place.location.coordinates.length === 2
    ) {
      const [lng, lat] = place.location.coordinates;
      dispatch(
        setLocationAction({
          location: {
            lat: Number(lat),
            lng: Number(lng),
          },
        }),
      );
    }
  };

  return {
    isLoading,
    nearByPlace,
    errorAlert,
    sweetAlert,
    alertProps,
    setSweetAlert,
    handleSave,
    handlePlaceSelect,
    callEndpoint,
    token,
  };
};
